/* eslint-disable */
/*!
* @scbd/self-embedding-component 0.0.25
*
* @link www.cbd.int
* @source https://github.com/scbd/ecosystem/packages/self-embedding-component
* @copyright (c) 2019-2020 Secretariat of the Convention on Biological Diversity <it@cbd.int>
* @license MIT
* https://github.com/scbd/ecosystem/blob/master/LICENCE
*/

import { pascalCase, camelCase } from 'change-case';
import { getDefaultOptionsFunction, setDefaultOptions } from '@scbd/default-options';

var name = "@scbd/self-embedding-component";

const cdn = 'https://cdn.cbd.int';

var getDefaultOptions = getDefaultOptionsFunction(name);

const dependencyRef = {
  VUE          : { url: `${cdn}/vue/dist/vue.esm.browser.min.js`, name: 'Vue' },
  VUE_I18N     : { url: `${cdn}/vue-i18n/dist/vue-i18n.esm.browser.min.js`, name: 'VueI18n' },
  SCBD_SSO_AUTH: { url: `${cdn}/@scbd/sso-vue-plugin-scbd`, name: 'sso-vue-plugin-scbd' },
  legacy       : {
    VUE          : { url: `${cdn}/vue`, name: 'Vue' },
    VUE_I18N     : { url: `${cdn}/vue-i18n`, name: 'VueI18n' },
    SCBD_SSO_AUTH: { url: `${cdn}/@scbd/sso-vue-plugin-scbd/dist/legacy/umd/index.umd.js`, name: 'sso-vue-plugin-scbd' }
  }
};

const validationMap = {
  id           : String,
  appId        : String,
  name         : String,
  selfId       : String,
  compName     : String,
  version      : String,
  propsData    : Object,
  cdnUrl       : String,
  cdn          : String,
  dependencies : Object,
  dependencyRef: Object,
  selfUrl      : String
};

const dependencies = { all: [],  css: [], vuePlugins: [], legacy: [] };

const dev     = { dependencyRef,  cdn, dependencies  };
const stg     = { ...dev };
const prod    = { ...stg };

const environments  = { prod, stg, dev };

setDefaultOptions({ environments, validationMap, name });

const parentNode  = '';
const selfElement = '';

let opts = {};

const createAppDiv = () => {
  const divTag = document.createElement('div');

  divTag.id = opts.appId;

  insertElement(divTag);
};

const  insertElement = (el) => {
  opts.parentNode.insertBefore(el, opts.selfElement);
};

const build = (options, legacy=false) => {
  if(!options.name) throw new Error('EmbedComponent requires name to be set in options')

  const regX = new RegExp('(@.+/)', 'ig');

  opts = { ...getDefaultOptions(options), parentNode, selfElement,
           get selfId(){ return this.id? this.id : this.name.replace(regX, '') },
           set selfId(id){ this.id = id; },
           get appId(){ return `appId-${this.selfId}` },
           get compName(){ return pascalCase((this.selfId)) },
           get cdnUrl(){ return this.version? `${this.cdn}/${this.name}@${this.version}` : `${this.cdn}/${this.name}` } };

  normalizeVuePlugins();
  initElements(legacy);
  loadCss();

  return opts
};

const initElements = (legacy) => {
  const elementId = legacy? `${opts.selfId}-legacy` : opts.selfId;

  opts.selfElement = document.getElementById(elementId);

  if(!opts.selfElement) throw new Error(`Id on script tag not found: id="${elementId}"`)

  opts.propsData.options = attrsAsOptions();
  opts.parentNode        = opts.selfElement.parentNode;
};

function attrsAsOptions(){
  const   self         = opts.selfElement;
  const { options }    = opts.propsData;
  const   attrs        = self.attributes;
  const   newOptions   = {};

  if (!self.hasAttributes()) return options

  for (const { name, value } of attrs){
    if([ 'id', 'src', 'type' ].includes(name)) continue

    newOptions[camelCase(name)]=value;
  }
  
  return Object.assign(options, newOptions)
}

function loadCss(){
  const { dependencies: { css } } = opts;

  if(css && css.length)
    css.forEach(cssUrl => loadLink(cssUrl));
}

function loadLink (url){
  const tag = document.createElement('link');

  tag.rel     = 'stylesheet';
  tag.charset = 'utf-8';
  tag.href    = url;

  insertElement(tag);
}

function normalizeVuePlugins(){
  const { dependencies      :{ all, vuePlugins } } = opts;

  if(!vuePlugins || !vuePlugins.length) return

  const allNames = all.map(mapByName);

  for (const plugin of vuePlugins)
    if(!allNames.includes(plugin.name))
      all.push(plugin);
}

function mapByName({ name }){
  return name
}

const loadScript = (url) => {
  const tag = document.createElement('script');

  tag.charset  = 'utf-8';
  tag.nomodule = true;
  tag.src      = url;

  const promiseFn = (resolve, reject) => {
    insertElement(tag);
    tag.onload  = () => resolve(true);
    tag.onerror = () => reject(false);
  };

  return new Promise(promiseFn)
};

const buildLegacyWidget = async ( theComponent, options = {}, plugins ) => {
  const opts                     = build(options, true);
  const { dependencies:{ all } } = opts;

  if(all && all.length)
    for (let i = 0; i < all.length; i++)
      await loadScript(all[i].url);
  
  loadVuePlugins(plugins);
  loadApp(theComponent);
};

function mounted (){
  const i18n                      = getI18n();
  const { propsData }             = opts;
  const VueClass                  = window['Vue'].extend(this.$options.theComponent);
  const classInstanceProps        = i18n? { i18n, propsData } : { propsData };
  const vueClassInstance          = new VueClass(classInstanceProps);

  vueClassInstance.$mount();

  this.$el.appendChild(vueClassInstance.$el);
}


const loadApp =  (theComponent) => {
  createAppDiv();

  new window['Vue']({ mounted, theComponent }).$mount(`#${opts.appId}`);
};

function getI18n(){
  if(window['VueI18n'])
    return new window['VueI18n']({ locale: 'en', fallbackLocale: 'en', messages: { en: {} } })
  return undefined
}

function loadVuePlugins(plugins){ // eslint-disable-line
  const { dependencies:{ vuePlugins }, propsData: { forceEnv } } = opts;
  const pluginOptionsDefault = { forceEnv };

  if(vuePlugins && vuePlugins.length) 
    for (let i = 0; i < vuePlugins.length; i++)
      if(!window[vuePlugins[i].name]){ throw new Error(`${vuePlugins[i].name} not found on global object`) }
      else {
        const plugin = window[vuePlugins[i].name];

        if(plugin.install)
          window.Vue.use(plugin, { ...vuePlugins[i].options||{}, ...pluginOptionsDefault });
        else
        if(plugin.default && plugin.default.install)
          window.Vue.use(plugin.default, { ...vuePlugins[i].options||{}, ...pluginOptionsDefault });
      }

  if(plugins && plugins.length)
    for (const plugin of plugins)
      if(plugin.install)
        window.Vue.use(plugin, { ...pluginOptionsDefault });
      else if(plugin.default && plugin.default.install)
        window.Vue.use(plugin.default, { ...pluginOptionsDefault });
}

function mounted$1 (){
  const i18n                      = getI18n$1();
  const { compName, propsData }   = opts;
  const VueClass                  = window['Vue'].extend(window[compName]);

  const classInstanceProps        = i18n? { i18n, propsData } : { propsData };
  const vueClassInstance          = new VueClass(classInstanceProps);

  vueClassInstance.$mount();
  
  this.$el.appendChild(vueClassInstance.$el);
}

const loadSelf =  () => {
  const { cdnUrl, compName, selfUrl } = opts;

  return loadScript$1({ url: selfUrl || cdnUrl, name: compName })
};


const loadApp$1 =  () => {
  createAppDiv();

  new window['Vue']({ mounted: mounted$1 }).$mount(`#${opts.appId}`);
};

const loadScript$1 = (dep) => {
  const { name, url } = dep;

  if(!window[name])
    return import(url).then((resolvedDep) => window[name]=resolvedDep.default? resolvedDep.default : resolvedDep)
  return window[name]
};

const buildWidget = async (options = {}) => {
  const opts = build(options);

  const { dependencies:{ all } } = opts;
  const   dependencyPromises     = [];

  if(all)
    for (let i = 0; i < all.length; i++)
      dependencyPromises.push(loadScript$1(all[i]));

  await Promise.all(dependencyPromises);

  loadVuePlugins$1();
  await loadSelf();
  loadApp$1();
};

function loadVuePlugins$1(){
  const { dependencies:{ vuePlugins }, propsData: { forceEnv } } = opts;
  const pluginOptionsDefault = { forceEnv };

  if(!vuePlugins) return
  for (let i = 0; i < vuePlugins.length; i++)
    if(!window[vuePlugins[i].name]){ throw new Error(`${vuePlugins[i].name} not found on global object`) }
    else {
      const plugin = window[vuePlugins[i].name];

      if(plugin.install)
        window.Vue.use(plugin, { ...vuePlugins[i].options||{}, ...pluginOptionsDefault });
      else
      if(plugin.default && plugin.default.install)
        window.Vue.use(plugin.default, { ...vuePlugins[i].options||{}, ...pluginOptionsDefault });
    }
}

function getI18n$1(){
  if(window['VueI18n'])
    return new window['VueI18n']({ locale: 'en', fallbackLocale: 'en', messages: { en: {} } })
  return undefined
}

export { buildLegacyWidget, buildWidget, dependencyRef };
//# sourceMappingURL=index.js.map
